import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const emptyForm = () => ({
  name: '',
  street_address: '',
  city: '',
  state: 'WA',
  zip_code: '',
  has_diesel: true,
  has_def: true,
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  parent_company: '',
  notes: '',
  is_active: true,
});

const ManagerDepots = () => {
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const loadDepots = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/depots/?include_inactive=1');
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setDepots(data);
    } catch (e) {
      console.error('Failed to load depots:', e?.response?.status, e?.response?.data || e);
      const status = e?.response?.status;
      const payload = e?.response?.data;
      const detail =
        (payload && typeof payload === 'string' && payload) ||
        payload?.detail ||
        payload?.error ||
        payload?.message;
      const isHtmlErrorPage = typeof detail === 'string' && detail.trim().startsWith('<!doctype html');

      if (status === 401 || status === 403) {
        setError('You are not authorized to load fuel depots. Please sign in again as a manager.');
      } else if (status === 404) {
        setError('Fuel depot API endpoint not found. Verify backend route `/api/depots/` is available.');
      } else if (status >= 500) {
        setError(
          isHtmlErrorPage
            ? 'Server error loading fuel depots. If depots are newly added, run backend migrations (`python manage.py migrate`).'
            : detail ||
              'Server error loading fuel depots. If depots are newly added, run backend migrations (`python manage.py migrate`).'
        );
      } else if (e?.code === 'ERR_NETWORK') {
        setError('Could not reach the API server. Check that the backend is running and reachable.');
      } else {
        setError(detail || 'Failed to load fuel depots.');
      }
      setDepots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDepots();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      street_address: row.street_address || '',
      city: row.city || '',
      state: row.state || 'WA',
      zip_code: row.zip_code || '',
      has_diesel: !!row.has_diesel,
      has_def: !!row.has_def,
      contact_name: row.contact_name || '',
      contact_phone: row.contact_phone || '',
      contact_email: row.contact_email || '',
      parent_company: row.parent_company || '',
      notes: row.notes || '',
      is_active: row.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    const payload = {
      name: form.name.trim(),
      street_address: form.street_address.trim(),
      city: form.city.trim(),
      state: (form.state || '').trim().toUpperCase(),
      zip_code: form.zip_code.trim(),
      has_diesel: form.has_diesel,
      has_def: form.has_def,
      contact_name: form.contact_name.trim(),
      contact_phone: form.contact_phone.trim(),
      contact_email: form.contact_email.trim(),
      parent_company: form.parent_company.trim(),
      notes: form.notes.trim(),
      is_active: form.is_active,
    };
    try {
      if (editingId) {
        await api.patch(`/depots/${editingId}/`, payload);
        setSuccess('Depot updated.');
      } else {
        await api.post('/depots/', payload);
        setSuccess('Depot created.');
      }
      setDialogOpen(false);
      await loadDepots();
    } catch (e) {
      const msg =
        e.response?.data && typeof e.response.data === 'object'
          ? JSON.stringify(e.response.data)
          : e.response?.data?.detail || 'Save failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this depot? It will disappear from maps until reactivated.')) return;
    setError('');
    try {
      await api.delete(`/depots/${id}/`);
      setSuccess('Depot deactivated.');
      await loadDepots();
    } catch (e) {
      setError('Failed to deactivate depot.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">Fuel depots</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add depot
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Refuel locations for your fleet. Depots appear on customer, driver, and manager maps for everyone who is
          logged in.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Parent company</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Fuels</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {depots.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No depots yet. Add one to show it on all maps.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                depots.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.name}</TableCell>
                    <TableCell>{d.parent_company}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 280 }}>
                        {d.full_address}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {d.has_diesel && <Chip size="small" label="Diesel" sx={{ mr: 0.5 }} />}
                      {d.has_def && <Chip size="small" label="DEF" />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        {d.contact_name || '—'}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {d.contact_phone || ''} {d.contact_email || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={d.is_active ? 'Active' : 'Inactive'}
                        color={d.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(d)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {d.is_active && (
                        <IconButton size="small" onClick={() => handleDeactivate(d.id)} aria-label="Deactivate">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingId ? 'Edit fuel depot' : 'Add fuel depot'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Depot name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                fullWidth
                helperText="Short label shown on maps (e.g. Main St cardlock)"
              />
              <TextField
                label="Parent company / operator"
                value={form.parent_company}
                onChange={(e) => setForm({ ...form, parent_company: e.target.value })}
                required
                fullWidth
                helperText="Brand or operator (Shell, Love’s, company-owned bulk tank, etc.)"
              />
              <TextField
                label="Street address"
                value={form.street_address}
                onChange={(e) => setForm({ ...form, street_address: e.target.value })}
                required
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="City"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  select
                  label="State"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  sx={{ width: 100 }}
                >
                  {US_STATES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="ZIP"
                  value={form.zip_code}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                  required
                  sx={{ width: 140 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.has_diesel}
                      onChange={(e) => setForm({ ...form, has_diesel: e.target.checked })}
                    />
                  }
                  label="Diesel"
                />
                <FormControlLabel
                  control={
                    <Checkbox checked={form.has_def} onChange={(e) => setForm({ ...form, has_def: e.target.checked })} />
                  }
                  label="DEF"
                />
              </Box>
              <Typography variant="subtitle2">Contact (optional)</Typography>
              <TextField
                label="Contact name"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Phone"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                fullWidth
              />
              <TextField
                label="Internal notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
              {editingId && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    />
                  }
                  label="Active (shown on maps)"
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ManagerDepots;
